// frontend/src/components/GlobalJobCreateFlyout.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-08-26
// Updated:      2026-10-08 10:22 BST
// 
// Change Log:
//   2026-10-20 08:27 BST: Use each Work Package draft location rather than parent Job location.
//   2026-10-08 10:22 BST: Added unrestricted historic/future Monday selection for Week Commencing and fixed JSX wrapper regression.
//   2026-10-07 15:42 BST: Added Job contains Work Packages and shared Work Package composer.
//
// Description:
//   System-wide job create flyout. Mirrors JobFormWidget persistence:
//   • Job is created via POST /api/jobs/ (baseURL: "/api")
//   • Resource + Planned start/end persisted via POST /api/jobs/{id}/resources
//   • Location persisted on job payload (with aliases) and, if still missing,
//     attached post-create using robust association endpoints + PUT/PATCH fallback.
//   • 15-minute time selection via custom date + quarter-hour <select>s (00/15/30/45).
//   • Job Ref evidence-only uniqueness check with debounce; disables Create on true duplicate.
//   • Supports week-based planning via nullable `week_commencing` instead of strict start/end dates.
//   • Keeps the existing desktop Create Job tab, while the shared mobile CSS
//     suppresses that duplicate launcher when PWA-003 bottom navigation is active.
//   • Removes frontend-supplied tenant_id, processed_by and tenantId values.
//     Authenticated backend context is the sole authority for identity/tenancy.
//   • Prefill context only switches to exact scheduling when explicit scheduling
//     data is supplied; Quote/Opportunity/commercial prefill preserves the user's
//     Week Commencing planning mode.

//
// Job Ref uniqueness correction (2026-09-29):
//  • Replaced legacy multi-endpoint evidence probes with the canonical
//    authenticated GET /jobs/ref/check?job_ref=... endpoint.
//  • Tenant context remains backend-owned; no tenant_id is sent by this client.
//  • Database uniqueness remains the final authority during create races.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaClipboardList } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import axiosInstance from "../api/axiosInstance";
import DocumentUploadAccordion from "./DocumentUploadAccordion.jsx";
import ExpenseCreateAccordion from "./ExpenseCreateAccordion.jsx";
import LocationEditModal from "./LocationEditModal.jsx";
import WorkPackageComposer from "./WorkPackageComposer.jsx";

const BUILD_TAG = "GJCF-2026-09-02-Q14B-COMMERCIAL-SELECTORS";

const DEFAULT_USE_WEEK_COMMENCING = true;

// -------------------- Small helpers --------------------
const isoNoMs = (v) => {
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().replace(/\.\d{3}Z$/, "Z");
  } catch {
    return "";
  }
};

const labelStyle = { display: "block", fontSize: 12, marginBottom: 6, color: "#333" };
const hintStyle = { fontSize: 11, color: "#6b7280", marginTop: 4 };
const inputStyle = {
  width: "100%",
  border: "1px solid #ccc",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 14,
  background: "#fff",
};

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const grid1a = { display: "grid", gridTemplateColumns: "1fr auto", gap: 8 };

const pad2 = (n) => String(n).padStart(2, "0");
const roundMinutesToQuarter = (mins) => Math.max(0, Math.min(59, Math.round(mins / 15) * 15));
const nextQuarterLocalTime = (d = new Date()) => {
  const hh = d.getHours();
  const mm = d.getMinutes();
  const rounded = roundMinutesToQuarter(mm);
  let rh = hh;
  let rm = rounded;
  if (rounded <= mm) {
    rm = (rounded + 15) % 60;
    if (rm === 0) rh = (hh + 1) % 24;
  }
  return `${pad2(rh)}:${pad2(rm)}`;
};
const quarterTimes = Array.from({ length: 24 * 4 }, (_, i) => {
  const hh = Math.floor(i / 4);
  const mm = (i % 4) * 15;
  return `${pad2(hh)}:${pad2(mm)}`;
});
const combineLocalDateTime = (dateStr, timeStr) =>
  dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr) && timeStr && /^\d{2}:\d{2}$/.test(timeStr)
    ? `${dateStr}T${timeStr}`
    : "";
const splitLocalDateTime = (dtStr) => {
  if (!dtStr || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dtStr)) return { date: "", time: "" };
  const [date, time] = dtStr.split("T");
  return { date, time };
};

const toLocalDateOnly = (d) => {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const addDaysLocal = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const startOfMonday = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const jsDay = x.getDay(); // 0 Sun ... 6 Sat
  const diff = jsDay === 0 ? -6 : 1 - jsDay;
  x.setDate(x.getDate() + diff);
  return x;
};

const buildWeekCommencingOptions = (monthsAhead = 3) => {
  const today = new Date();
  const firstMonday = startOfMonday(today);
  const end = new Date(today);
  end.setMonth(end.getMonth() + monthsAhead);

  const out = [];
  let cur = new Date(firstMonday);

  while (cur <= end) {
    out.push({
      value: toLocalDateOnly(cur),
      label: `Mon ${cur.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`,
    });
    cur = addDaysLocal(cur, 7);
  }

  return out;
};

// util: detect duplicate/unique errors from backend payloads that surface as 409/500
function looksLikeDuplicate(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const txt =
    (typeof data === "string" && data) ||
    (data?.detail && String(data.detail)) ||
    (data && JSON.stringify(data)) ||
    "";
  if (status === 409) return true;
  if (status === 422 && /duplicate|unique/i.test(txt)) return true;
  if (status === 500 && /duplicate|unique|constraint|integrity/i.test(txt)) return true;
  return false;
}

function listFrom(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.quotes)) return data.quotes;
  if (Array.isArray(data.opportunities)) return data.opportunities;
  return [];
}

function sortByNameAsc(rows) {
  const arr = Array.isArray(rows) ? [...rows] : [];
  arr.sort((a, b) => {
    const an = (a?.name || "").toString();
    const bn = (b?.name || "").toString();
    return an.localeCompare(bn, undefined, { sensitivity: "base" });
  });
  return arr;
}

function sortContactsAsc(rows) {
  const arr = Array.isArray(rows) ? [...rows] : [];
  arr.sort((a, b) => {
    const al = (a?.last_name || "").toString();
    const bl = (b?.last_name || "").toString();
    const lc = al.localeCompare(bl, undefined, { sensitivity: "base" });
    if (lc) return lc;
    const af = (a?.first_name || "").toString();
    const bf = (b?.first_name || "").toString();
    const fc = af.localeCompare(bf, undefined, { sensitivity: "base" });
    if (fc) return fc;
    const ae = (a?.email || "").toString();
    const be = (b?.email || "").toString();
    return ae.localeCompare(be, undefined, { sensitivity: "base" });
  });
  return arr;
}

async function fetchAllPaged(path, { baseURL = "/api", limit = 200, maxPages = 50 } = {}) {
  const out = [];
  let offset = 0;

  for (let page = 0; page < maxPages; page++) {
    const sep = path.includes("?") ? "&" : "?";
    const url = `${path}${sep}limit=${limit}&offset=${offset}`;
    const { data } = await axiosInstance.get(url, { baseURL });

    const items = listFrom(data);
    out.push(...items);

    const total = Number(data?.total);
    const pageLimit = Number(data?.limit) || limit;

    if (!Number.isFinite(total) || total <= 0) break;

    offset += pageLimit;
    if (out.length >= total) break;
    if (items.length === 0) break;
  }

  return out;
}

// Quote -> Job context helpers
const normaliseMatchText = (v) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const JOB_TYPE_MATCH_STOP_WORDS = new Set([
  "survey",
  "surveys",
  "service",
  "services",
  "work",
  "works",
  "job",
  "jobs",
  "traffic",
  "count",
  "counts",
  "data",
  "site",
]);

const matchWords = (v) =>
  normaliseMatchText(v)
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);

const suggestJobTypeId = (rows, hint) => {
  const h = normaliseMatchText(hint);
  if (!h) return "";

  const hintWords = matchWords(h);
  const distinctiveHintWords = hintWords.filter(
    (word) => word.length >= 3 && !JOB_TYPE_MATCH_STOP_WORDS.has(word)
  );

  let best = null;
  let bestScore = 0;

  for (const row of Array.isArray(rows) ? rows : []) {
    const candidates = [row?.name, row?.code, row?.short_code, row?.abbreviation]
      .map(normaliseMatchText)
      .filter(Boolean);

    for (const candidate of candidates) {
      let score = 0;

      if (h === candidate) {
        score = 100;
      } else if (
        h.includes(` ${candidate} `) ||
        h.startsWith(`${candidate} `) ||
        h.endsWith(` ${candidate}`)
      ) {
        score = candidate.length <= 4 ? 80 : 90;
      } else if (candidate.length >= 4 && h.includes(candidate)) {
        score = 70;
      }

      const candidateWords = matchWords(candidate);
      const distinctiveCandidateWords = candidateWords.filter(
        (word) => word.length >= 3 && !JOB_TYPE_MATCH_STOP_WORDS.has(word)
      );

      const sharedDistinctive = distinctiveHintWords.filter((word) =>
        distinctiveCandidateWords.includes(word)
      );

      if (sharedDistinctive.length) {
        const longestShared = Math.max(...sharedDistinctive.map((word) => word.length));
        const sharedScore = 72 + Math.min(18, sharedDistinctive.length * 6 + longestShared);
        score = Math.max(score, sharedScore);
      }

      if (score > bestScore) {
        bestScore = score;
        best = row;
      }
    }
  }

  return bestScore >= 70 && best?.id ? String(best.id) : "";
};

// -------------------------------------------------------

export default function GlobalJobCreateFlyout(props) {
  const { theme } = useTheme();

  const weekCommencingOptions = useMemo(() => buildWeekCommencingOptions(3), []);

  const Button = ({ children, onClick, type = "general", style, disabled = false, title = "" }) => {
    const group = theme[type === "action" ? "button_action" : "button_general"] || {};
    const bg = group.normal || "#111";
    return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        title={title}
        style={{
          background: bg,
          color: "#fff",
          border: "none",
          padding: "10px 14px",
          borderRadius: 8,
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.65 : 1,
          ...style,
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.background = group.hover || bg;
        }}
        onMouseLeave={(e) => {
          if (!disabled) e.currentTarget.style.background = bg;
        }}
        onMouseDown={(e) => {
          if (!disabled) e.currentTarget.style.background = group.click || group.hover || bg;
        }}
        onMouseUp={(e) => {
          if (!disabled) e.currentTarget.style.background = group.hover || bg;
        }}
      >
        {children}
      </button>
    );
  };

  // UI state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Data lists
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsByCustomer, setContactsByCustomer] = useState({});
  const [jobTypes, setJobTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypes2, setSubtypes2] = useState([]);
  const [resources, setResources] = useState([]);
  const [containsWorkPackages, setContainsWorkPackages] = useState(false);
  const [workPackageDrafts, setWorkPackageDrafts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commercialError, setCommercialError] = useState("");

  // Context supplied by the launching Quote/Opportunity surface.
  const [launchContext, setLaunchContext] = useState({
    quote_id: "",
    opportunity_id: "",
    job_type_hint: "",
    quote_line_items: [],
  });

  // Form state
  const [form, setForm] = useState({
    job_ref: "",
    customer_id: "",
    contact_id: "",
    opportunity_id: "",
    quote_id: "",
    job_type_id: "",
    job_subtype_id: "",
    job_subtype2_id: "",
    priority: "Normal",
    planned_start: "",
    planned_end: "",
    week_commencing: weekCommencingOptions[0]?.value || "",
    use_week_commencing: DEFAULT_USE_WEEK_COMMENCING,
    location_id: "",
    resource_id: "",
    co_resource_id: "",
    site_name: "",
    description: "",
    due_date: "",
  });

  // Custom date/time UI (quarter-hour)
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  // Docs & expenses
  const [customerRef, setCustomerRef] = useState("");
  const [docTags, setDocTags] = useState([]);
  const [expenseDrafts, setExpenseDrafts] = useState([]);

  // Location modal
  const [locModalOpen, setLocModalOpen] = useState(false);

  const flyoutRef = useRef(null);

  useEffect(() => {
    window.__GJCF_BUILD = BUILD_TAG;
  }, []);

  // Bridge + event listener (mount once)
  useEffect(() => {
    const EVENT =
      (typeof props?.prefillEventName === "string" && props.prefillEventName) ||
      "calendar:prefill-job";

    window.__GJCF_PREFILL = (payload = {}) => {
      window.__GJCF_PREFILL_CACHE = payload;

      setLaunchContext({
        quote_id: payload.quote_id != null ? String(payload.quote_id) : "",
        opportunity_id: payload.opportunity_id != null ? String(payload.opportunity_id) : "",
        job_type_hint: typeof payload.job_type_hint === "string" ? payload.job_type_hint : "",
        quote_line_items: Array.isArray(payload.quote_line_items) ? payload.quote_line_items : [],
      });

      setForm((f) => ({
        ...f,
        customer_id:
          payload.customer_id !== null && typeof payload.customer_id !== "undefined"
            ? String(payload.customer_id)
            : "",
        contact_id:
          payload.contact_id !== null && typeof payload.contact_id !== "undefined"
            ? String(payload.contact_id)
            : "",
        opportunity_id:
          payload.opportunity_id !== null && typeof payload.opportunity_id !== "undefined"
            ? String(payload.opportunity_id)
            : "",
        quote_id:
          payload.quote_id !== null && typeof payload.quote_id !== "undefined"
            ? String(payload.quote_id)
            : "",
        job_type_id:
          payload.job_type_id !== null && typeof payload.job_type_id !== "undefined"
            ? String(payload.job_type_id)
            : "",
        job_ref:
          typeof payload.job_ref === "string"
            ? payload.job_ref
            : "",
        site_name:
          typeof payload.site_name === "string"
            ? payload.site_name
            : "",
        description:
          typeof payload.description === "string"
            ? payload.description
            : "",
      }));

      setOpen(true);
      setStep(1);
    };

    const onPrefill = (e) => window.__GJCF_PREFILL(e.detail || {});
    window.addEventListener(EVENT, onPrefill);
    return () => window.removeEventListener(EVENT, onPrefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate Step 3 when we get there (if prefilled).
  // Commercial/context-only prefill must not change the user's planning mode.
  // Switch to exact scheduling only when explicit schedule/resource data exists.
  useEffect(() => {
    if (step !== 3) return;
    const p = window.__GJCF_PREFILL_CACHE;
    if (!p) return;

    setForm((f) => {
      const hasExactSchedulePrefill =
        !!p.starts_at ||
        !!p.ends_at ||
        (p.resource_id !== null &&
          typeof p.resource_id !== "undefined" &&
          p.resource_id !== "");

      const next = {
        ...f,
        ...(hasExactSchedulePrefill ? { use_week_commencing: false } : {}),
      };

      if (p.starts_at) {
        const s = new Date(p.starts_at);
        if (!isNaN(s.getTime())) {
          const y = s.getFullYear(),
            m = pad2(s.getMonth() + 1),
            d = pad2(s.getDate());
          const hh = pad2(s.getHours()),
            mm = pad2(roundMinutesToQuarter(s.getMinutes()));
          next.planned_start = `${y}-${m}-${d}T${hh}:${mm}`;
          setStartDate(`${y}-${m}-${d}`);
          setStartTime(`${hh}:${mm}`);
        }
      }
      if (p.ends_at) {
        const e = new Date(p.ends_at);
        if (!isNaN(e.getTime())) {
          const y = e.getFullYear(),
            m = pad2(e.getMonth() + 1),
            d = pad2(e.getDate());
          const hh = pad2(e.getHours()),
            mm = pad2(roundMinutesToQuarter(e.getMinutes()));
          next.planned_end = `${y}-${m}-${d}T${hh}:${mm}`;
          setEndDate(`${y}-${m}-${d}`);
          setEndTime(`${hh}:${mm}`);
        }
      }
      if (
        p.resource_id !== null &&
        typeof p.resource_id !== "undefined" &&
        p.resource_id !== ""
      ) {
        next.resource_id = String(p.resource_id);
      }
      return next;
    });
  }, [step]);

  // Bootstrap lists
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [custRowsRaw, jtRes] = await Promise.all([
          fetchAllPaged("/customers", { baseURL: "/api", limit: 200 }),
          axiosInstance.get("/job-types?sort=name:asc", { baseURL: "/api" }),
        ]);
        if (!alive) return;

        const custRows = sortByNameAsc(custRowsRaw);
        setCustomers(custRows);
        setJobTypes(listFrom(jtRes.data));
      } catch (e) {
        console.error("[GJCF] bootstrap fetch failed:", e?.response?.data || e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Q-14B: Load tenant-visible commercial provenance lists.
  useEffect(() => {
    let alive = true;

    (async () => {
      setCommercialLoading(true);
      setCommercialError("");

      const [opportunityResult, quoteResult] = await Promise.allSettled([
        axiosInstance.get("/opportunities?limit=500&offset=0", { baseURL: "/api" }),
        axiosInstance.get("/quotes?limit=1000&offset=0&sort=updated_at:desc", { baseURL: "/api" }),
      ]);

      if (!alive) return;

      setOpportunities(
        opportunityResult.status === "fulfilled"
          ? listFrom(opportunityResult.value?.data)
          : []
      );
      setQuotes(
        quoteResult.status === "fulfilled"
          ? listFrom(quoteResult.value?.data)
          : []
      );

      const failed = [];
      if (opportunityResult.status === "rejected") failed.push("Opportunities");
      if (quoteResult.status === "rejected") failed.push("Quotes");
      setCommercialError(
        failed.length ? `${failed.join(" and ")} could not be loaded.` : ""
      );
      setCommercialLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const customerScopedOpportunities = useMemo(() => {
    if (!form.customer_id) return [];

    return [...opportunities]
      .filter((o) => String(o?.customer_id ?? "") === String(form.customer_id))
      .sort((a, b) =>
        String(a?.title || a?.name || `Opportunity ${a?.id ?? ""}`).localeCompare(
          String(b?.title || b?.name || `Opportunity ${b?.id ?? ""}`),
          undefined,
          { sensitivity: "base" }
        )
      );
  }, [opportunities, form.customer_id]);

  const customerScopedQuotes = useMemo(() => {
    if (!form.customer_id) return [];

    return [...quotes]
      .filter((q) => String(q?.customer_id ?? "") === String(form.customer_id))
      .filter((q) => {
        if (!form.opportunity_id) return true;
        return String(q?.opportunity_id ?? "") === String(form.opportunity_id);
      })
      .sort((a, b) => {
        const ad = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const bd = new Date(b?.updated_at || b?.created_at || 0).getTime();
        if (ad !== bd) return bd - ad;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
  }, [quotes, form.customer_id, form.opportunity_id]);

  const handleCustomerChange = (customerId) => {
    setForm((f) => ({
      ...f,
      customer_id: customerId,
      contact_id: "",
      opportunity_id: "",
      quote_id: "",
    }));
    setLaunchContext((ctx) => ({
      ...ctx,
      quote_id: "",
      opportunity_id: "",
    }));
  };

  const handleOpportunityChange = (opportunityId) => {
    setForm((f) => {
      const currentQuote = quotes.find(
        (q) => String(q?.id) === String(f.quote_id || "")
      );
      const quoteStillCompatible =
        !currentQuote ||
        !currentQuote?.opportunity_id ||
        String(currentQuote.opportunity_id) === String(opportunityId || "");

      return {
        ...f,
        opportunity_id: opportunityId,
        quote_id: quoteStillCompatible ? f.quote_id : "",
      };
    });
    setLaunchContext((ctx) => ({
      ...ctx,
      opportunity_id: opportunityId,
      quote_id:
        quotes.find((q) => String(q?.id) === String(form.quote_id || ""))?.opportunity_id &&
        String(
          quotes.find((q) => String(q?.id) === String(form.quote_id || ""))?.opportunity_id
        ) !== String(opportunityId || "")
          ? ""
          : ctx.quote_id,
    }));
  };

  const handleQuoteChange = (quoteId) => {
    const selectedQuote = quotes.find(
      (q) => String(q?.id) === String(quoteId || "")
    );

    setForm((f) => ({
      ...f,
      quote_id: quoteId,
      opportunity_id: selectedQuote?.opportunity_id
        ? String(selectedQuote.opportunity_id)
        : quoteId
        ? ""
        : f.opportunity_id,
    }));

    setLaunchContext((ctx) => ({
      ...ctx,
      quote_id: quoteId,
      opportunity_id: selectedQuote?.opportunity_id
        ? String(selectedQuote.opportunity_id)
        : quoteId
        ? ""
        : ctx.opportunity_id,
    }));
  };

  // Suggest a Job Type from Quote title/line-item text only when the
  // launching Quote did not supply an explicit Job Type.
  useEffect(() => {
    if (form.job_type_id || !launchContext.job_type_hint || !jobTypes.length) return;
    const suggested = suggestJobTypeId(jobTypes, launchContext.job_type_hint);
    if (suggested) setForm((f) => (f.job_type_id ? f : { ...f, job_type_id: suggested }));
  }, [jobTypes, launchContext.job_type_hint, form.job_type_id]);

  // Load ALL contacts once, bucket by customer
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let rows = [];
        try {
          rows = await fetchAllPaged("/contacts", { baseURL: "/api", limit: 250 });
        } catch {
          const res = await axiosInstance.get("/contacts", { baseURL: "/api" });
          rows = listFrom(res.data);
        }

        if (!alive) return;

        const sorted = sortContactsAsc(rows);
        setContacts(sorted);

        const bucket = {};
        for (const c of sorted) {
          const cid = Number(c.customer_id) || 0;
          if (!bucket[cid]) bucket[cid] = [];
          bucket[cid].push(c);
        }
        setContactsByCustomer(bucket);
      } catch (e) {
        console.error("[GJCF] contacts fetch failed:", e?.response?.data || e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load resources
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/resources", { baseURL: "/api" });
        if (!alive) return;
        const list = listFrom(res.data).length ? listFrom(res.data) : listFrom(res.data?.items || []);
        const norm = list.map((r) => ({
          id: Number(r.id),
          label:
            (r.name && String(r.name)) ||
            [r.first_name, r.last_name].filter(Boolean).join(" ") ||
            r.identifier ||
            r.email ||
            `Resource ${r.id}`,
        }));
        setResources(norm);
      } catch (e) {
        console.error("[GJCF] resources fetch failed:", e?.response?.data || e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load locations
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/locations", { baseURL: "/api" });
        if (!alive) return;
        const rows = listFrom(res.data).length ? listFrom(res.data) : listFrom(res.data?.items || []);
        setLocations(rows || []);
      } catch (e) {
        console.error("[GJCF] locations fetch failed:", e?.response?.data || e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // When job_type changes, load subtypes
  useEffect(() => {
    const id = Number(form.job_type_id);
    setForm((f) => ({ ...f, job_subtype_id: "", job_subtype2_id: "" }));
    setSubtypes([]);
    setSubtypes2([]);
    if (!id) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/job-subtypes?job_type_id=${id}&sort=name:asc`, {
          baseURL: "/api",
        });
        setSubtypes(listFrom(res.data));
      } catch (e) {
        console.warn("[GJCF] subtypes fetch failed:", e?.response?.data || e?.message);
      }
    })();
  }, [form.job_type_id]);

  // When job_subtype changes, load subtypes2
  useEffect(() => {
    const id = Number(form.job_subtype_id);
    setForm((f) => ({ ...f, job_subtype2_id: "" }));
    setSubtypes2([]);
    if (!id) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/job-subtypes2?job_subtype_id=${id}&sort=name:asc`, {
          baseURL: "/api",
        });
        setSubtypes2(listFrom(res.data));
      } catch (e) {
        console.warn("[GJCF] subtypes2 fetch failed:", e?.response?.data || e?.message);
      }
    })();
  }, [form.job_subtype_id]);

  const filteredContacts = useMemo(() => {
    const cid = Number(form.customer_id) || 0;
    if (!cid) return [];
    if (contactsByCustomer[cid]) return contactsByCustomer[cid];
    return contacts.filter((c) => Number(c.customer_id) === cid);
  }, [form.customer_id, contacts, contactsByCustomer]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setCustomerRef("");
      const cid = form.customer_id;
      if (!cid) return;
      try {
        const { data } = await axiosInstance.get(`/customers/${encodeURIComponent(cid)}`, {
          baseURL: "/api",
        });
        if (!alive) return;
        if (data?.internal_reference) setCustomerRef(String(data.internal_reference));
      } catch (err) {
        console.warn("[GJCF] could not detect internal_reference", err?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [form.customer_id]);

  // ---------------- Job Ref live uniqueness check (debounced) ----------------
  const reqSeq = useRef(0);
  const [jobRefCheck, setJobRefCheck] = useState({
    status: "idle",
    duplicate: false,
    id: null,
    msg: "",
    probedAny: false,
  });
  const norm = (s) => (s || "").trim();

  useEffect(() => {
    const ref = norm(form.job_ref);
    if (!ref) {
      setJobRefCheck({
        status: "idle",
        duplicate: false,
        id: null,
        msg: "",
        probedAny: false,
      });
      return;
    }

    const mySeq = ++reqSeq.current;
    setJobRefCheck((s) => ({
      ...s,
      status: "checking",
      msg: "",
      duplicate: false,
      id: null,
      probedAny: false,
    }));

    const check = async () => {
      try {
        const { data } = await axiosInstance.get("/jobs/ref/check", {
          baseURL: "/api",
          params: { job_ref: ref },
        });

        if (reqSeq.current !== mySeq) return;

        const duplicate = data?.exists === true;
        const id = data?.id ?? null;

        setJobRefCheck({
          status: "ok",
          duplicate,
          id,
          msg: duplicate
            ? `Job ref already exists${id ? ` (ID ${id})` : ""}.`
            : "",
          probedAny: true,
        });
      } catch (err) {
        if (reqSeq.current !== mySeq) return;

        console.warn("[GJCF] Job Ref uniqueness check failed", err?.message);
        setJobRefCheck({
          status: "unknown",
          duplicate: false,
          id: null,
          msg: "",
          probedAny: false,
        });
      }
    };

    const t = setTimeout(check, 400);
    return () => clearTimeout(t);
  }, [form.job_ref]);

  // ---------------- Quarter-hour date/time wiring ----------------
  useEffect(() => {
    if (!startDate && !startTime && !endDate && !endTime) {
      const now = new Date();
      const sT = nextQuarterLocalTime(now);
      const sD = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
      const endObj = new Date(now.getTime() + 60 * 60 * 1000);
      const eT = nextQuarterLocalTime(endObj);
      const eD = `${endObj.getFullYear()}-${pad2(endObj.getMonth() + 1)}-${pad2(endObj.getDate())}`;

      setStartDate(sD);
      setStartTime(sT);
      setEndDate(eD);
      setEndTime(eT);
      setForm((f) => ({
        ...f,
        planned_start: combineLocalDateTime(sD, sT),
        planned_end: combineLocalDateTime(eD, eT),
      }));
    }
  }, [startDate, startTime, endDate, endTime]);

  useEffect(() => {
    if (form.planned_start) {
      const { date, time } = splitLocalDateTime(form.planned_start);
      if (date && time) {
        setStartDate(date);
        setStartTime(time);
      }
    }
    if (form.planned_end) {
      const { date, time } = splitLocalDateTime(form.planned_end);
      if (date && time) {
        setEndDate(date);
        setEndTime(time);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStartDateChange = (v) => {
    setStartDate(v);
    setForm((f) => ({ ...f, planned_start: combineLocalDateTime(v, startTime) }));
  };
  const onStartTimeChange = (v) => {
    setStartTime(v);
    setForm((f) => ({ ...f, planned_start: combineLocalDateTime(startDate, v) }));
  };
  const onEndDateChange = (v) => {
    setEndDate(v);
    setForm((f) => ({ ...f, planned_end: combineLocalDateTime(v, endTime) }));
  };
  const onEndTimeChange = (v) => {
    setEndTime(v);
    setForm((f) => ({ ...f, planned_end: combineLocalDateTime(endDate, v) }));
  };

  const onUseWeekCommencingChange = (checked) => {
    setForm((f) => ({
      ...f,
      use_week_commencing: checked,
      week_commencing: checked ? (f.week_commencing || weekCommencingOptions[0]?.value || "") : "",
      resource_id: checked ? "" : f.resource_id,
      co_resource_id: checked ? "" : f.co_resource_id,
    }));
  };

  const attachLocationAfterCreate = async (jobId, locId) => {
    if (!jobId || !locId) return;

    try {
      const { data } = await axiosInstance.get(`/jobs/${jobId}`, { baseURL: "/api" });
      const current = Number(data?.location_id) || null;
      if (current === Number(locId)) {
        console.info("[GJCF] Location already persisted on job:", current);
        return;
      }
    } catch (e) {
      console.warn("[GJCF] pre-attach GET job failed (continue):", e?.response?.status, e?.response?.data || e?.message);
    }

    const trySteps = [
      async () => {
        await axiosInstance.put(
          `/jobs/${jobId}`,
          { location_id: Number(locId) },
          { baseURL: "/api", headers: { "Content-Type": "application/json" } }
        );
      },
      async () => {
        await axiosInstance.patch(
          `/jobs/${jobId}`,
          { location_id: Number(locId) },
          { baseURL: "/api", headers: { "Content-Type": "application/json" } }
        );
      },
      async () => {
        await axiosInstance.post(
          `/jobs/${jobId}/location`,
          { location_id: Number(locId) },
          { baseURL: "/api", headers: { "Content-Type": "application/json" } }
        );
      },
      async () => {
        await axiosInstance.post(
          `/job-locations`,
          { job_id: Number(jobId), location_id: Number(locId) },
          { baseURL: "/api", headers: { "Content-Type": "application/json" } }
        );
      },
      async () => {
        await axiosInstance.post(
          `/jobs/${jobId}/locations`,
          { location_id: Number(locId) },
          { baseURL: "/api", headers: { "Content-Type": "application/json" } }
        );
      },
    ];

    for (const step of trySteps) {
      try {
        await step();
        try {
          const { data } = await axiosInstance.get(`/jobs/${jobId}`, { baseURL: "/api" });
          const persisted = Number(data?.location_id) || null;
          if (persisted === Number(locId)) {
            console.info("[GJCF] location persisted via step");
            return;
          }
        } catch (e) {
          console.warn("[GJCF] verify GET failed (continue):", e?.response?.status, e?.response?.data || e?.message);
        }
      } catch (e) {
        console.warn("[GJCF] attach step failed:", e?.response?.status, e?.response?.data || e?.message);
      }
    }
    console.warn("[GJCF] location still not persisted after attach cascade");
  };

  const createAssignmentIfProvided = async (jobId, snap) => {
    if (!jobId) return;
    if (snap?.use_week_commencing) return;

    const rid = snap?.resource_id ? Number(snap.resource_id) : null;
    const coRid = snap?.co_resource_id ? Number(snap.co_resource_id) : null;
    const start = snap?.planned_start;
    const end = snap?.planned_end;
    if (!rid || !start || !end) return;

    const starts_at = isoNoMs(start);
    const ends_at = isoNoMs(end);
    if (!starts_at || !ends_at) return;

    const body = { resource_id: rid, starts_at, ends_at, status: "assigned" };

    try {
      const res = await axiosInstance.post(`/jobs/${jobId}/resources`, body, {
        baseURL: "/api",
        headers: { "Content-Type": "application/json" },
      });

      if (coRid) {
        const createdId =
          (res?.data && (Number(res.data.id) || Number(res.data.assignment_id))) || null;

        const patchCo = async (assignmentId) => {
          await axiosInstance.patch(
            `/job-assignments/${assignmentId}`,
            { co_resource_id: coRid },
            { baseURL: "/api", headers: { "Content-Type": "application/json" } }
          );
        };

        if (createdId) {
          await patchCo(createdId);
        } else {
          try {
            const list = await axiosInstance.get(`/job-assignments?job_id=${jobId}`, {
              baseURL: "/api",
            });
            const items = Array.isArray(list.data?.items) ? list.data.items : [];
            const match = items.find(
              (a) =>
                Number(a.resource_id) === rid &&
                isoNoMs(a.starts_at) === starts_at &&
                isoNoMs(a.ends_at) === ends_at
            );
            if (match?.id) await patchCo(match.id);
          } catch (e) {
            console.warn("[GJCF] job-assignments lookup failed:", e?.response?.status, e?.response?.data || e?.message);
          }
        }
      }
    } catch (e) {
      console.warn(
        "[GJCF] assignment create failed:",
        e?.response?.status,
        e?.response?.data || e?.message
      );
    }
  };

  const createJob = async () => {
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    const snap = { ...form };

    try {
      if (!snap.job_ref || !snap.customer_id || !snap.job_type_id) {
        setErrorMsg("Job Reference, Customer and Job Type are required.");
        setSaving(false);
        return;
      }

      if (containsWorkPackages && !workPackageDrafts.length) {
        setErrorMsg("Add at least one Work Package or untick Job contains Work Packages.");
        setSaving(false);
        setStep(2);
        return;
      }
      if (containsWorkPackages && workPackageDrafts.some((row) => !row.binding)) {
        setErrorMsg("Every Work Package must resolve to a published workflow binding before the Job can be created.");
        setSaving(false);
        setStep(2);
        return;
      }

      if (jobRefCheck.duplicate) {
        setErrorMsg(jobRefCheck.msg || "Job reference already exists.");
        setSaving(false);
        return;
      }

      if (snap.use_week_commencing && !snap.week_commencing) {
        setErrorMsg("Week commencing is required when week-based planning is enabled.");
        setSaving(false);
        setStep(3);
        return;
      }

      if (snap.use_week_commencing && snap.week_commencing) {
        const selectedWeek = new Date(`${snap.week_commencing}T12:00:00`);
        if (Number.isNaN(selectedWeek.getTime()) || selectedWeek.getDay() !== 1) {
          setErrorMsg("Week Commencing must be a Monday.");
          setSaving(false);
          setStep(3);
          return;
        }
      }

      if (!snap.use_week_commencing && (snap.resource_id || snap.co_resource_id) && (!snap.planned_start || !snap.planned_end)) {
        setErrorMsg("To assign a Resource or Co-Resource at creation, set Planned Start and Planned End.");
        setSaving(false);
        setStep(3);
        return;
      }

      const rawLoc = snap.location_id;
      const locVal =
        rawLoc !== "" && rawLoc !== null && typeof rawLoc !== "undefined"
          ? Number(rawLoc)
          : null;

      const baseJobRef = snap.job_ref.trim();
      const firstPayload = {
        job_ref: baseJobRef || `JOB-${Date.now().toString().slice(-6)}`,
        customer_id: Number(snap.customer_id),
        contact_id: snap.contact_id ? Number(snap.contact_id) : null,
        opportunity_id: snap.opportunity_id ? Number(snap.opportunity_id) : null,
        quote_id: snap.quote_id ? Number(snap.quote_id) : null,
        job_type_id: snap.job_type_id ? Number(snap.job_type_id) : null,
        job_subtype_id: snap.job_subtype_id ? Number(snap.job_subtype_id) : null,
        job_subtype2_id: snap.job_subtype2_id ? Number(snap.job_subtype2_id) : null,
        priority: snap.priority || "Normal",
        week_commencing: snap.use_week_commencing ? snap.week_commencing || null : null,
        location_id: locVal,
        site_location_id: locVal ?? null,
        site_id: locVal ?? null,
      };

      let res;
      try {
        res = await axiosInstance.post("/jobs/", firstPayload, {
          baseURL: "/api",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        });
      } catch (err) {
        if (looksLikeDuplicate(err)) {
          const retryRef = `JOB-${Date.now().toString().slice(-9)}`;
          const retryPayload = { ...firstPayload, job_ref: retryRef };
          console.warn("[GJCF] duplicate/unique error detected; retrying with job_ref:", retryRef);
          res = await axiosInstance.post("/jobs/", retryPayload, {
            baseURL: "/api",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
          });
        } else {
          throw err;
        }
      }

      const newJobId = res?.data?.id;

      if (newJobId && locVal) {
        try {
          await attachLocationAfterCreate(newJobId, locVal);
        } catch (e) {
          console.warn("[GJCF] post-create location attach failed:", e?.response?.status, e?.response?.data || e?.message);
        }
      }

      await createAssignmentIfProvided(newJobId, snap);

      if (newJobId && containsWorkPackages && workPackageDrafts.length) {
        for (const draft of workPackageDrafts) {
          const wpRes = await axiosInstance.post(`/jobs/${newJobId}/work-packages`, {
            title: draft.title,
            package_type: draft.package_type || "work_package",
            work_package_type_id: Number(draft.work_package_type_id),
            work_package_subtype_id: draft.work_package_subtype_id ? Number(draft.work_package_subtype_id) : null,
            work_package_subtype2_id: draft.work_package_subtype2_id ? Number(draft.work_package_subtype2_id) : null,
            location_id: draft.location_id ? Number(draft.location_id) : null,
            assigned_resource_id: draft.assigned_resource_id ? Number(draft.assigned_resource_id) : null,
            week_commencing: snap.use_week_commencing ? snap.week_commencing || null : null,
            planned_start: snap.use_week_commencing ? null : snap.planned_start || null,
            planned_end: snap.use_week_commencing ? null : snap.planned_end || null,
            is_required: true,
          }, { baseURL: "/api" });
          await axiosInstance.post(`/jobs/${newJobId}/work-packages/${wpRes.data.id}/workflow/instantiate`, { force_template_version_id: draft.binding?.workflow_template_version_id || null }, { baseURL: "/api" });
        }
      }

      if (newJobId && expenseDrafts.length) {
        try {
          const toPost = expenseDrafts.map((p) => {
            const safeDraft = { ...(p || {}) };
            delete safeDraft.tenant_id;
            delete safeDraft.user_id;
            delete safeDraft.processed_by;

            return {
              ...safeDraft,
              job_id: Number(newJobId),
              customer_id:
                typeof safeDraft.customer_id !== "undefined" &&
                safeDraft.customer_id !== null
                  ? safeDraft.customer_id
                  : snap.customer_id
                  ? Number(snap.customer_id)
                  : null,
              resource_id:
                typeof safeDraft.resource_id === "number"
                  ? safeDraft.resource_id
                  : !snap.use_week_commencing && snap.resource_id
                  ? Number(snap.resource_id)
                  : null,
            };
          });
          await Promise.allSettled(
            toPost.map((row) =>
              axiosInstance.post("/expenses", row, {
                baseURL: "/api",
                headers: { "Content-Type": "application/json" },
              })
            )
          );
        } catch {
          /* ignore */
        }
      }

      setSuccessMsg(`Job created (ID ${newJobId ?? "?"})`);
      try {
        window.dispatchEvent(new CustomEvent("calendar:refresh"));
      } catch {}

      setOpen(false);
      setStep(1);
      setForm((f) => ({
        ...f,
        job_ref: "",
        customer_id: "",
        contact_id: "",
        opportunity_id: "",
        quote_id: "",
        job_type_id: "",
        job_subtype_id: "",
        job_subtype2_id: "",
        planned_start: "",
        planned_end: "",
        week_commencing: weekCommencingOptions[0]?.value || "",
        use_week_commencing: DEFAULT_USE_WEEK_COMMENCING,
        resource_id: "",
        co_resource_id: "",
        location_id: "",
        site_name: "",
        description: "",
        due_date: "",
      }));
      setContainsWorkPackages(false);
      setWorkPackageDrafts([]);
      setDocTags([]);
      setExpenseDrafts([]);
      setCustomerRef("");
      setLaunchContext({ quote_id: "", opportunity_id: "", job_type_hint: "", quote_line_items: [] });
      window.__GJCF_PREFILL_CACHE = null;

      setStartDate("");
      setStartTime("");
      setEndDate("");
      setEndTime("");
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.error("[GJCF] create job error:", status, data || e?.message);
      setErrorMsg(typeof data?.detail === "string" ? data.detail : "Network or server error creating job.");
    } finally {
      setSaving(false);
    }
  };

  const handleLocationCreated = (loc) => {
    setLocations((rows) => [{ ...loc }, ...(rows || [])]);
    setForm((f) => ({ ...f, location_id: String(loc.id) }));
  };

  const friendlyLocation = (l) =>
    (l?.name && String(l.name)) ||
    [l?.address_line_1, l?.postcode].filter(Boolean).join(", ") ||
    (l?.id ? `Location ${l.id}` : "Location");

  const jobRefBorderColor = jobRefCheck.duplicate
    ? "#ef4444"
    : jobRefCheck.status === "checking"
    ? "#f59e0b"
    : "#ccc";

  return (
    <>
      {/* Right-side vertical tab */}
      <div
        className="global-job-create-launcher"
        onClick={() => setOpen(true)}
        title="Create Job"
        style={{
          position: "fixed",
          right: 0,
          top: "45%",
          transform: "translateY(-50%)",
          zIndex: 150600,
          background: theme?.button_action?.normal || "#111",
          color: "#fff",
          width: 44,
          height: 160,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = theme?.button_action?.hover || "#333")}
        onMouseLeave={(e) => (e.currentTarget.style.background = theme?.button_action?.normal || "#111")}
      >
        <div
          style={{
            transform: "rotate(-90deg)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FaClipboardList />
          <span style={{ fontWeight: 700, whiteSpace: "nowrap", fontFamily: "var(--font-body)" }}>Create Job</span>
        </div>
      </div>

      {/* Dim overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 150550 }}
        />
      )}

      {/* Flyout */}
      <aside
        ref={flyoutRef}
        data-build={BUILD_TAG}
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100dvh",
          width: "min(560px, 92vw)",
          transform: open ? "translateX(0)" : "translateX(105%)",
          transition: "transform 200ms ease",
          zIndex: 150601,
          display: "flex",
          alignItems: "flex-start",
          pointerEvents: open ? "auto" : "none",
        }}
        aria-hidden={!open}
      >
        <div
          style={{
            marginTop: "10vh",
            marginBottom: "10vh",
            height: "80vh",
            width: "100%",
            background: "#fff",
            borderLeft: "1px solid #e5e7eb",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontWeight: 700 }}>Create a Job</div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer" }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Steps */}
          <div style={{ padding: 16, overflow: "auto" }}>
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-3">
                <div>
                  <label style={labelStyle}>Job Reference (required)</label>
                  <input
                    style={{ ...inputStyle, borderColor: jobRefBorderColor }}
                    value={form.job_ref}
                    onChange={(e) => setForm({ ...form, job_ref: e.target.value })}
                    onBlur={() => setForm((f) => ({ ...f, job_ref: (f.job_ref || "").trim() }))}
                    placeholder="e.g. 3501"
                  />
                  {jobRefCheck.status === "checking" && <div style={{ ...hintStyle, color: "#b45309" }}>Checking…</div>}
                  {!!(!jobRefCheck.duplicate && jobRefCheck.status === "unknown") && (
                    <div style={hintStyle}>Couldn’t verify uniqueness right now; you can still proceed.</div>
                  )}
                  {jobRefCheck.duplicate && <div style={{ ...hintStyle, color: "#b91c1c" }}>{jobRefCheck.msg || "Job ref already exists."}</div>}
                </div>

                <div>
                  <label style={labelStyle}>Customer (required)</label>
                  <select
                    style={inputStyle}
                    value={form.customer_id}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                  >
                    <option value="">-- Select Customer --</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Contact (filtered)</label>
                  <select
                    style={inputStyle}
                    value={form.contact_id}
                    onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                    disabled={!form.customer_id || !filteredContacts.length}
                  >
                    <option value="">-- Select Contact --</option>
                    {filteredContacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} — {c.email}
                      </option>
                    ))}
                  </select>
                  {!!form.customer_id && !filteredContacts.length && (
                    <div style={hintStyle}>No contacts returned for this customer (API filtering/pagination issue).</div>
                  )}
                </div>

                <div style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: 10, background: "#f9fafb" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                    Commercial provenance
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Opportunity (optional)</label>
                    <select
                      style={inputStyle}
                      value={form.opportunity_id}
                      onChange={(e) => handleOpportunityChange(e.target.value)}
                      disabled={!form.customer_id || commercialLoading}
                    >
                      <option value="">-- No Opportunity --</option>
                      {customerScopedOpportunities.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.title || o.name || `Opportunity #${o.id}`} — Opportunity #{o.id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Quote (optional)</label>
                    <select
                      style={inputStyle}
                      value={form.quote_id}
                      onChange={(e) => handleQuoteChange(e.target.value)}
                      disabled={!form.customer_id || commercialLoading}
                    >
                      <option value="">-- No Quote --</option>
                      {customerScopedQuotes.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.title || q.quote_ref || `Quote #${q.id}`} — Quote #{q.id}
                          {q.status ? ` — ${q.status}` : ""}
                        </option>
                      ))}
                    </select>
                    {!!form.customer_id && !!form.opportunity_id && (
                      <div style={hintStyle}>
                        Quotes are filtered to this customer and Opportunity.
                      </div>
                    )}
                  </div>

                  {commercialLoading && (
                    <div style={hintStyle}>Loading Opportunities and Quotes…</div>
                  )}
                  {commercialError && (
                    <div style={{ ...hintStyle, color: "#b45309" }}>{commercialError}</div>
                  )}
                  {(launchContext.quote_id || launchContext.opportunity_id) && (
                    <div style={hintStyle}>
                      Preselected from the launching Quote/Opportunity. Review or change before creating the Job.
                    </div>
                  )}
                </div>

                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 12, border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={containsWorkPackages} onChange={(e) => { setContainsWorkPackages(e.target.checked); if (!e.target.checked) setWorkPackageDrafts([]); }} style={{ width: 20, height: 20, marginTop: 1 }} />
                  <span><strong style={{ display: "block" }}>Job contains Work Packages</strong><span style={hintStyle}>Work Package resources may remain Unassigned during workforce planning.</span></span>
                </label>

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button onClick={() => setOpen(false)} style={{ background: "#666" }}>
                    Cancel
                  </Button>
                  <Button type="action" onClick={() => setStep(2)} style={{ marginLeft: "auto" }}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-3">
                <div>
                  <label style={labelStyle}>Job Type (required)</label>
                  <select style={inputStyle} value={form.job_type_id} onChange={(e) => setForm({ ...form, job_type_id: e.target.value })}>
                    <option value="">-- Select Type --</option>
                    {jobTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {launchContext.job_type_hint && form.job_type_id ? (
                    <div style={hintStyle}>Suggested from the originating Quote. Review or change before creating the Job.</div>
                  ) : null}
                </div>

                <div>
                  <label style={labelStyle}>Subtype</label>
                  <select
                    style={inputStyle}
                    value={form.job_subtype_id}
                    onChange={(e) => setForm({ ...form, job_subtype_id: e.target.value })}
                    disabled={!subtypes.length}
                  >
                    <option value="">-- Select Subtype --</option>
                    {subtypes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Subtype 2</label>
                  <select
                    style={inputStyle}
                    value={form.job_subtype2_id}
                    onChange={(e) => setForm({ ...form, job_subtype2_id: e.target.value })}
                    disabled={!subtypes2.length}
                  >
                    <option value="">-- Select Subtype 2 --</option>
                    {subtypes2.map((s2) => (
                      <option key={s2.id} value={s2.id}>
                        {s2.name}
                      </option>
                    ))}
                  </select>
                </div>

                {containsWorkPackages && (
                  <WorkPackageComposer
                    jobTypeName={jobTypes.find((t) => String(t.id) === String(form.job_type_id))?.name || ""}
                    resources={resources}
                    value={workPackageDrafts}
                    onChange={setWorkPackageDrafts}
                  />
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button onClick={() => setStep(1)}>Back</Button>
                  <Button type="action" onClick={() => setStep(3)} style={{ marginLeft: "auto" }}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 — Schedule & Resourcing */}
            {step === 3 && (
              <div className="space-y-3">
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 10,
                    background: "#fafafa",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!form.use_week_commencing}
                      onChange={(e) => onUseWeekCommencingChange(e.target.checked)}
                    />
                    Use Week Commencing instead of specific start and end dates
                  </label>
                  <div style={hintStyle}>
                    Recommended for operational planning where the exact day/time is not fixed at job creation.
                  </div>
                </div>

                {form.use_week_commencing ? (
                  <div>
                    <label style={labelStyle}>Week Commencing</label>
                    <input
                      type="date"
                      style={inputStyle}
                      value={form.week_commencing}
                      onChange={(e) => setForm((f) => ({ ...f, week_commencing: e.target.value }))}
                    />
                    <div style={hintStyle}>
                      Select the Monday for the job week. Historic and future weeks are supported when exact working dates are not known.
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={labelStyle}>Planned Start</label>
                      <div style={grid2}>
                        <input type="date" style={inputStyle} value={startDate} onChange={(e) => onStartDateChange(e.target.value)} />
                        <select style={inputStyle} value={startTime} onChange={(e) => onStartTimeChange(e.target.value)}>
                          {quarterTimes.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Planned End</label>
                      <div style={grid2}>
                        <input type="date" style={inputStyle} value={endDate} onChange={(e) => onEndDateChange(e.target.value)} />
                        <select style={inputStyle} value={endTime} onChange={(e) => onEndTimeChange(e.target.value)}>
                          {quarterTimes.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label style={labelStyle}>Assign Resource (optional)</label>
                  <select
                    style={{
                      ...inputStyle,
                      background: form.use_week_commencing ? "#f3f4f6" : "#fff",
                      color: form.use_week_commencing ? "#6b7280" : "#111827",
                    }}
                    value={form.resource_id}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        resource_id: e.target.value,
                        co_resource_id: f.co_resource_id === e.target.value ? "" : f.co_resource_id,
                      }))
                    }
                    disabled={form.use_week_commencing}
                  >
                    <option value="">-- No Resource --</option>
                    {resources.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <div style={hintStyle}>
                    {form.use_week_commencing
                      ? "Disabled in week commencing mode. Allocate the job later in planner when the exact slot is known."
                      : "Requires Planned Start & End."}
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Assign Co-Resource (optional)</label>
                  <select
                    style={{
                      ...inputStyle,
                      background: form.use_week_commencing ? "#f3f4f6" : "#fff",
                      color: form.use_week_commencing ? "#6b7280" : "#111827",
                    }}
                    value={form.co_resource_id}
                    onChange={(e) => setForm({ ...form, co_resource_id: e.target.value })}
                    disabled={form.use_week_commencing}
                  >
                    <option value="">-- None --</option>
                    {resources
                      .filter((r) => String(r.id) !== String(form.resource_id || ""))
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label}
                        </option>
                      ))}
                  </select>
                  <div style={hintStyle}>
                    {form.use_week_commencing
                      ? "Disabled in week commencing mode."
                      : "Requires Planned Start & End."}
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Priority</label>
                  <select style={inputStyle} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <option>Low</option>
                    <option>Normal</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button onClick={() => setStep(2)}>Back</Button>
                  <Button type="action" onClick={() => setStep(4)} style={{ marginLeft: "auto" }}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4 — Site/Location + Documents + Expenses */}
            {step === 4 && (
              <div className="space-y-3">
                <div>
                  <label style={labelStyle}>Site Name</label>
                  <input style={inputStyle} value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} />
                </div>

                <div>
                  <label style={labelStyle}>Location (optional)</label>
                  <div style={grid1a}>
                    <select style={inputStyle} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                      <option value="">-- Not set --</option>
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {friendlyLocation(l)}
                        </option>
                      ))}
                    </select>

                    <Button type="action" onClick={() => setLocModalOpen(true)} style={{ minWidth: 160 }}>
                      Create Location
                    </Button>
                  </div>

                  <LocationEditModal open={locModalOpen} onClose={() => setLocModalOpen(false)} onCreated={handleLocationCreated} initial={{}} />
                </div>

                {/* Documents */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Documents</div>

                  <div style={{ ...grid2, marginBottom: 10 }}>
                    <div>
                      <label style={labelStyle}>Customer Ref (required for uploads)</label>
                      <input
                        style={inputStyle}
                        placeholder="e.g., CUST-0001"
                        value={customerRef}
                        onChange={(e) => setCustomerRef(e.target.value)}
                      />
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                        Auto-detected from customers.internal_reference — you can override it here.
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Job Ref (optional)</label>
                      <input
                        style={inputStyle}
                        placeholder="e.g., JOB-123456"
                        value={form.job_ref}
                        onChange={(e) => setForm((f) => ({ ...f, job_ref: e.target.value }))}
                        onBlur={() => setForm((f) => ({ ...f, job_ref: (f.job_ref || "").trim() }))}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>Document Tags</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      {(docTags || []).map((t) => (
                        <span
                          key={t}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 8px",
                            borderRadius: 999,
                            border: "1px solid #d1d5db",
                            background: "#f9fafb",
                            fontSize: 13,
                          }}
                        >
                          {t}
                          <span
                            role="button"
                            onClick={() => setDocTags((arr) => arr.filter((x) => x !== t))}
                            title="Remove"
                            style={{ cursor: "pointer", padding: "0 4px", color: "#666", userSelect: "none" }}
                          >
                            ×
                          </span>
                        </span>
                      ))}
                    </div>
                    <input
                      style={inputStyle}
                      placeholder="Type a tag and press Enter"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = e.currentTarget.value.trim();
                          if (v && !docTags.includes(v)) setDocTags((arr) => [...arr, v]);
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                  </div>

                  <DocumentUploadAccordion
                    customerId={form.customer_id || ""}
                    jobId={""}
                    customerRef={customerRef}
                    jobRef={form.job_ref || ""}
                    defaultTagsCsv={(docTags || []).join(",")}
                    defaultPublish={false}
                  />
                </div>

                {/* Expenses (shared, deferred mode) */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Expenses</div>
                  <ExpenseCreateAccordion
                    title="Add Expenses for this job (queued until create)"
                    customerId={form.customer_id || null}
                    defaultResourceId={!form.use_week_commencing ? form.resource_id || "" : ""}
                    onDraftsChange={setExpenseDrafts}
                    accent={theme?.accent_color || theme?.header_bg || "#0e2a3f"}
                  />
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <Button onClick={() => setStep(3)}>Back</Button>
                  <Button type="action" onClick={() => setStep(5)} style={{ marginLeft: "auto" }}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5 — Confirm */}
            {step === 5 && (
              <div className="space-y-3">
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Confirm</div>
                <pre
                  style={{
                    background: "#f7f7f9",
                    border: "1px solid #eee",
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 12,
                    maxHeight: 160,
                    overflow: "auto",
                  }}
                >
{JSON.stringify(form, null, 2)}
                </pre>

                {/* Queued expenses summary */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Queued Expenses</div>
                  {expenseDrafts.length ? (
                    <div>
                      <div style={{ marginBottom: 6 }}>
                        {expenseDrafts.length} item(s) — £
                        {expenseDrafts.reduce((s, d) => s + (Number(d.amount) || 0), 0).toFixed(2)}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {expenseDrafts.map((x, i) => (
                          <li key={i} style={{ fontSize: 13, color: "#374151" }}>
                            £{Number(x.amount).toFixed(2)} {x.currency} • {x.category || "General"} • {x.description || "—"} • {x.occurred_at}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div style={{ color: "#6b7280" }}>None</div>
                  )}
                </div>

                {errorMsg && <div style={{ color: "crimson", fontSize: 13 }}>{errorMsg}</div>}
                {successMsg && <div style={{ color: "green", fontSize: 13 }}>{successMsg}</div>}

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button onClick={() => setStep(4)}>Back</Button>
                  <Button
                    type="action"
                    onClick={createJob}
                    style={{
                      marginLeft: "auto",
                      opacity: saving || jobRefCheck.duplicate ? 0.7 : 1,
                      cursor: saving || jobRefCheck.duplicate ? "not-allowed" : "pointer",
                    }}
                    disabled={saving || jobRefCheck.duplicate}
                    title={jobRefCheck.duplicate ? "Fix duplicate Job Ref first" : ""}
                  >
                    {saving ? "Creating…" : "Create Job"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
